import axios from 'axios';
import Vue from 'vue';

const state = {
    firstName: null,
    lastName: null,
    email: null,
    emailVerified: null,
    dossierData: {
        fieldValues: {}
    }
};

const getters = {
    /**
     * Returns a full name by combining first + last names.
     * If either name is missing, returns the one we have.
     * @returns {String} The full name, or an empty string.
     */
    fullName(state) {
        const { firstName, lastName } = state;
        let name = '';
        if (firstName && lastName) {
            name = `${firstName} ${lastName}`;
        } else if (firstName) {
            name = firstName;
        } else if (lastName) {
            name = lastName;
        }
        return name;
    },
    /**
     * Checks whether any of the dossier fields are currently validating
     * @param {Object} state Contains the current state of the profile data
     * @returns {Boolean} True if any of the dossier fields are invalid. False otherwise
     */
    isDossierFieldValidating: (state) => Object.keys(state.dossierData.fieldValues).some((id) => state.dossierData.fieldValues[id].validating),
    /**
     * Checks whether any of the dossier fields have failed validation
     * in their current state
     * @param {Object} state Contains the current state of the profile data
     * @returns {Boolean} True if any of the dossier fields are invalid. False otherwise
     */
    anyDossierFieldsInvalid: (state) => Object.keys(state.dossierData.fieldValues).some((id) => !state.dossierData.fieldValues[id].valid),
    /**
     * Returns the field values for the dossier fields
     * @param {Object} state Contains the current state of the profile data
     * @returns {Array} An array of field values
     */
    getDossierFieldValues: (state) => state.dossierData.fieldValues
};

const actions = {
    /**
     * Loads the user's profile data and commits it to this module's state.
     * @param {Object} context
     */
    async load({ commit, rootState }) {
        const { data } = await axios.get(`${rootState.homeUrl}/settings/profile`);
        commit('setProfile', data);
    },
    /**
     * Updates the user's profile with the provided data, and commits it to
     * this module's state if successful.
     * @param {Object} context
     * @param {Object} data The profile data, i.e. { firstName: 'Larry', ...}
     */
    async save({ rootState, dispatch }, data) {
        await axios.put(`${rootState.homeUrl}/settings/profile`, data);
        // Load fresh data so we're in sync with Keycloak
        await dispatch('load');
    },
    /**
     * Delete the user account.
     * @param {Object} context
     */
    async delete({ rootState }) {
        await axios.delete(`${rootState.homeUrl}/user`);
    }
};

const mutations = {
    /**
     * Updates this modules's state with the provided profile data.
     * @param {Object} state
     * @param {Object} profileData
     */
    setProfile(state, { firstName, lastName, email, emailVerified, dossierData = { fieldValues: {} } }) {
        state.firstName = firstName;
        state.lastName = lastName;
        state.email = email;
        state.emailVerified = emailVerified;
        state.dossierData = dossierData;
    },
    /**
     * Adds field value data for a given dossier field.
     * If the field value already exists, it will be updated.
     * @param {Object} state contains the current state of the profile data
     * @param {Object} payload profile field data
     */
    setFieldData(state, { fieldData }) {
        const dataToSet = {
            data: { ...fieldData.value },
            isEmpty: fieldData.isEmpty,
            valid: fieldData.valid,
            validating: fieldData.validating
        };

        Vue.set(state.dossierData.fieldValues, fieldData.fieldId, dataToSet);
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
